import React, { useContext, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputMask } from "primereact/inputmask";

import EntityFormPage from "../../../../components/EntityFormPage/EntityFormPage";
import { ToastContext } from "../../../../App";
import {
  useGetUserListQuery,
  useUpdateUserMutation,
} from "../../../../redux/services/user";
import getSubdomain from "../../../../utils/getSubdomain";

import * as styles from "../ChefManagement.module.css";

const LIST_PATH = "/app/manager/chefs";

function ChefEditPage() {
  const showToast = useContext(ToastContext);
  const navigate = useNavigate();
  const { id } = useParams();

  const { data: chefList } = useGetUserListQuery(getSubdomain());

  const chef = useMemo(
    () => chefList?.find((c) => String(c.id) === String(id)),
    [chefList, id],
  );

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { name: "", email: "", phone: "" },
  });

  useEffect(() => {
    if (chef) {
      reset({
        name: chef.username || "",
        email: chef.email || "",
        phone: chef.phone?.slice(2) || "",
      });
    }
  }, [chef, reset]);

  const [
    updateChef,
    {
      isSuccess: isUpdateChefSuccess,
      isError: isUpdateChefError,
      error: updateChefError,
    },
  ] = useUpdateUserMutation();

  useEffect(() => {
    if (isUpdateChefSuccess) {
      showToast("success", "Chef", "Chef updated!");
      navigate(LIST_PATH);
    }
  }, [isUpdateChefSuccess]);

  useEffect(() => {
    if (isUpdateChefError) {
      showToast(
        "error",
        updateChefError?.status,
        updateChefError?.data?.message ||
          (updateChefError?.data?.errors && "Incorrect data"),
      );
    }
  }, [isUpdateChefError]);

  const onSaveEdit = (data) => {
    updateChef({
      subdomain: getSubdomain(),
      id,
      payload: {
        name: data.name,
        phone: `+1${data.phone}`,
        email: data.email,
      },
    });
  };

  return (
    <EntityFormPage
      title="Edit Chef"
      backTo={LIST_PATH}
      footer={
        <Button
          label="Save"
          type="submit"
          className={`p-button-success ${styles.saveBtn}`}
          onClick={handleSubmit(onSaveEdit)}
        />
      }
    >
      <form onSubmit={handleSubmit(onSaveEdit)}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label>Name</label>
              <InputText
                className={`w-full mb-3 ${styles.input}`}
                placeholder="Name"
                {...field}
              />
            </div>
          )}
        />

        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label>Email</label>
              <InputText
                className={`w-full mb-3 ${styles.input}`}
                placeholder="Email"
                {...field}
              />
            </div>
          )}
        />

        <Controller
          name="phone"
          control={control}
          rules={{
            required: "Enter your phone number",
            minLength: { value: 10, message: "Phone number is invalid" },
          }}
          render={({ field, fieldState }) => (
            <div className="field">
              <label>Phone Number</label>
              <div>
                <div className="p-inputgroup flex-1">
                  <span
                    className={`p-inputgroup-addon mb-3 ${styles.phonePrefix}`}
                  >
                    +1
                  </span>
                  <InputMask
                    className={`w-full mb-3 ${styles.inputPhone} ${
                      fieldState.invalid ? "p-invalid" : ""
                    }`}
                    placeholder="(999) 999-9999"
                    prefix="+1"
                    mask="(999) 999-9999"
                    unmask={true}
                    {...field}
                  />
                </div>
                {fieldState.error && (
                  <small className="p-error">{fieldState.error.message}</small>
                )}
              </div>
            </div>
          )}
        />

        <button type="submit" hidden />
      </form>
    </EntityFormPage>
  );
}

export default ChefEditPage;
